import { localUrl as appLocalUrl } from '../app-path.js';
import SessionManager from '../session-manager';
import ApiClient from '../api-client';
import { FavoriteStorage, getGenericData } from './local-storage-functions';
import LocalStorageHelper from './local-storage-helper';

const LOGGED_FAVORITE_LIMIT = 2;
const PENDING_FAVORITE_KEY = 'climatempo_pending_favorite_city';
const FAVORITE_TYPE = 'city';
let serverSyncPromise = null;
let serverSyncShouldNotify = false;
let serverSyncNotificationSuppressed = false;
let serverFavorites = [];
let hasLoadedServerFavorites = false;

function normalizeFavorites(favorites = FavoriteStorage.get()) {
	if (!Array.isArray(favorites)) {
		return [];
	}

	return favorites.filter(favorite => favorite?.city);
}

function getCityStorageId(city = {}) {
	const id = city.cloc || city.idlocale || city.localeId || city.id || city.cid || city.idcity;
	return id === undefined || id === null || id === '' ? null : String(id);
}

function normalizeCityIdentityPart(value) {
	return String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.trim()
		.toLowerCase();
}

function getCitySemanticKey(city = {}) {
	const labelParts = String(city.label || '').split(/\s+-\s+/);
	const name =
		city.cname ||
		city.cityName ||
		city.localeName ||
		city.name ||
		labelParts[0] ||
		'';
	const uf =
		LocalStorageHelper.resolveLocationCode(city) ||
		(labelParts.length > 1 ? labelParts[labelParts.length - 1] : '');
	const normalizedName = normalizeCityIdentityPart(name);
	const normalizedUf = normalizeCityIdentityPart(uf);

	if (!normalizedName || !normalizedUf) {
		return null;
	}

	return `${normalizedName}|${normalizedUf}`;
}

function normalizeCityDisplay(city = {}) {
	const labelParts = String(city.label || '').split(/\s+-\s+/);
	const name =
		city.cname ||
		city.cityName ||
		city.localeName ||
		city.name ||
		labelParts[0] ||
		'';
	const locationCode = LocalStorageHelper.resolveLocationCode(city);
	const normalizedCity = { ...city };

	if (
		String(city.uf || '').toUpperCase() === 'ND' &&
		!city.ac &&
		locationCode &&
		locationCode !== 'ND'
	) {
		normalizedCity.ac = locationCode;
	}

	if (name && locationCode) {
		normalizedCity.label = `${name} - ${locationCode}`;
	}

	return normalizedCity;
}

function isSameCity(firstCity = {}, secondCity = {}) {
	const firstId = getCityStorageId(firstCity);
	const secondId = getCityStorageId(secondCity);

	if (firstId && secondId && firstId === secondId) {
		return true;
	}

	const firstSemanticKey = getCitySemanticKey(firstCity);
	const secondSemanticKey = getCitySemanticKey(secondCity);

	return Boolean(
		firstSemanticKey &&
		secondSemanticKey &&
		firstSemanticKey === secondSemanticKey,
	);
}

function getSubscriptionPlanName() {
	try {
		const ctMc = JSON.parse(localStorage.getItem('ct_mc') || '{}');
		return String(ctMc.mcsp || '').trim();
	} catch (e) {
		return '';
	}
}

function isClimatempoPlus() {
	if (!SessionManager.isAuthenticated()) {
		return false;
	}

	const planName = getSubscriptionPlanName();
	if (!planName) {
		return false;
	}

	return /(premium|plus|\+)/i.test(planName);
}

function getActiveLimit() {
	if (!SessionManager.isAuthenticated()) {
		return 0;
	}

	return isClimatempoPlus() ? Infinity : LOGGED_FAVORITE_LIMIT;
}

function getFavoritePayload(city) {
	return {
		...getGenericData('favorite'),
		city,
	};
}

function mapBackendFavoriteToCity(favorite = {}) {
	const name = favorite.cityName || favorite.localeName || favorite.name;
	const storedUf = favorite.uf || favorite.stateUf || '';
	const slug = favorite.slugCity || favorite.slug || '';
	const uf = LocalStorageHelper.resolveLocationCode({
		...favorite,
		uf: storedUf,
		slug,
	});
	const localeId = favorite.localeId || favorite.idlocale;
	const cityId = favorite.cityId || favorite.idcity || localeId;

	if (!name || !localeId) {
		return null;
	}

	return {
		cid: cityId,
		cloc: localeId,
		cname: name,
		label: uf ? `${name} - ${uf}` : name,
		uf: storedUf || uf,
		ac:
			favorite.ac ||
			(String(storedUf).toUpperCase() === 'ND' ? uf : ''),
		country: favorite.country,
		clat: favorite.latitude,
		clon: favorite.longitude,
		slug,
		backendCreatedAt: favorite.createdAt || null,
	};
}

function mapBackendFavorite(favorite = {}) {
	const city = mapBackendFavoriteToCity(favorite);
	if (!city) {
		return null;
	}

	return {
		id: favorite.id || null,
		city: normalizeCityDisplay(city),
		timestamp: favorite.createdAt
			? Date.parse(favorite.createdAt)
			: Date.now(),
	};
}

function getServerFavoriteIds() {
	return serverFavorites
		.map(favorite => getCityStorageId(favorite?.city))
		.filter(Boolean);
}

function getPendingFavorite() {
	try {
		const raw = sessionStorage.getItem(PENDING_FAVORITE_KEY);
		if (!raw) {
			return null;
		}

		const pending = JSON.parse(raw);
		if (!pending?.city || !getCityStorageId(pending.city)) {
			sessionStorage.removeItem(PENDING_FAVORITE_KEY);
			return null;
		}

		return pending;
	} catch (error) {
		console.error('Erro ao recuperar favorito pendente:', error);
		return null;
	}
}

function setPendingFavorite(city, reason, userId = SessionManager.getUserId()) {
	try {
		const pending = {
			city,
			reason,
			userId:
				userId === undefined || userId === null || userId === ''
					? null
					: String(userId),
			createdAt: Date.now(),
		};
		sessionStorage.setItem(PENDING_FAVORITE_KEY, JSON.stringify(pending));
		return pending;
	} catch (error) {
		console.error('Erro ao salvar favorito pendente:', error);
		return null;
	}
}

function clearPendingFavorite(city = null) {
	try {
		if (city) {
			const pending = getPendingFavorite();
			if (!pending || !isSameCity(pending.city, city)) {
				return;
			}
		}

		sessionStorage.removeItem(PENDING_FAVORITE_KEY);
	} catch (error) {
		console.error('Erro ao limpar favorito pendente:', error);
	}
}

function dispatchFavoriteChanged(city, isFavorited, reason = null) {
	const event = new CustomEvent('favoriteChanged', {
		detail: {
			city,
			isFavorited,
			reason,
		},
	});
	document.dispatchEvent(event);
}

function dispatchFavoriteSync() {
	document.dispatchEvent(new CustomEvent('favoriteSync'));
}

function notifyFavoriteAuthRequired(city) {
	document.dispatchEvent(
		new CustomEvent('favoriteAuthRequired', {
			detail: { city },
		}),
	);
	setTimeout(() => {
		showFavoriteRuleModal({
			type: 'auth',
			title: 'Este é seu primeiro favorito',
			body: 'Entre ou crie uma conta para salvar suas cidades favoritas.',
			primaryLabel: 'Fazer login',
			primaryHref: appLocalUrl('/login'),
			secondaryLabel: 'Criar conta',
			secondaryHref: appLocalUrl('/cadastro'),
		});
	}, 0);
}

function notifyFavoriteLimitReached(city) {
	document.dispatchEvent(
		new CustomEvent('favoriteLimitReached', {
			detail: {
				city,
				limit: LOGGED_FAVORITE_LIMIT,
			},
		}),
	);
	setTimeout(() => {
		showFavoriteRuleModal({
			type: 'limit',
			title: 'Limite de favoritos atingido',
			body: `Você pode salvar até ${LOGGED_FAVORITE_LIMIT} cidades favoritas. Assine Climatempo+ para favoritos ilimitados.`,
			primaryLabel: 'Conhecer Climatempo+',
			primaryHref: appLocalUrl('/planos'),
		});
	}, 0);
}

function ensureModalStyles() {
	if (document.getElementById('favorite-city-rule-modal-styles')) {
		return;
	}

	const style = document.createElement('style');
	style.id = 'favorite-city-rule-modal-styles';
	style.textContent = `
		.favorite-city-rule-modal {
			position: fixed;
			inset: 0;
			z-index: 10010;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 20px;
			background: rgba(0, 0, 0, .55);
		}

		.favorite-city-rule-modal[hidden] {
			display: none;
		}

		.favorite-city-rule-modal__dialog {
			position: relative;
			width: min(100%, 360px);
			padding: 28px 24px 24px;
			border-radius: 8px;
			background: #ffffff;
			color: #202124;
			box-shadow: 0 18px 40px rgba(0, 0, 0, .22);
			text-align: center;
		}

		.favorite-city-rule-modal__close {
			position: absolute;
			top: 8px;
			right: 8px;
			width: 32px;
			height: 32px;
			border: 0;
			background: transparent;
			color: #565658;
			font-size: 22px;
			line-height: 1;
			cursor: pointer;
		}

		.favorite-city-rule-modal__icon {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 42px;
			height: 42px;
			margin-bottom: 12px;
			border-radius: 50%;
			background: #f5f7fb;
			color: #e22558;
			font-size: 24px;
		}

		.favorite-city-rule-modal__title {
			margin: 0 0 8px;
			font-size: 18px;
			font-weight: 700;
			line-height: 1.25;
		}

		.favorite-city-rule-modal__body {
			margin: 0 0 18px;
			color: #565658;
			font-size: 14px;
			line-height: 1.45;
		}

		.favorite-city-rule-modal__actions {
			display: flex;
			flex-direction: column;
			gap: 10px;
		}

		.favorite-city-rule-modal__button {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			min-height: 40px;
			padding: 0 16px;
			border-radius: 4px;
			text-decoration: none;
			font-size: 14px;
			font-weight: 700;
		}

		.favorite-city-rule-modal__button.-primary {
			background: #e22558;
			color: #ffffff;
		}

		.favorite-city-rule-modal__button.-secondary {
			border: 1px solid #d6d9de;
			color: #202124;
		}
	`;
	document.head.appendChild(style);
}

function closeFavoriteRuleModal() {
	const modal = document.getElementById('favorite-city-rule-modal');
	if (modal) {
		modal.remove();
	}
}

function showFavoriteRuleModal({
	title,
	body,
	primaryLabel,
	primaryHref,
	secondaryLabel = '',
	secondaryHref = '',
}) {
	if (typeof document === 'undefined') {
		return;
	}

	ensureModalStyles();
	closeFavoriteRuleModal();

	const modal = document.createElement('div');
	modal.id = 'favorite-city-rule-modal';
	modal.className = 'favorite-city-rule-modal';
	modal.setAttribute('role', 'presentation');

	const secondaryButton = secondaryLabel
		? `<a class="favorite-city-rule-modal__button -secondary" href="${secondaryHref}">${secondaryLabel}</a>`
		: '';

	modal.innerHTML = `
		<div class="favorite-city-rule-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="favorite-city-rule-modal-title">
			<button type="button" class="favorite-city-rule-modal__close" aria-label="Fechar">&times;</button>
			<div class="favorite-city-rule-modal__icon" aria-hidden="true">♥</div>
			<p class="favorite-city-rule-modal__title" id="favorite-city-rule-modal-title">${title}</p>
			<p class="favorite-city-rule-modal__body">${body}</p>
			<div class="favorite-city-rule-modal__actions">
				<a class="favorite-city-rule-modal__button -primary" href="${primaryHref}">${primaryLabel}</a>
				${secondaryButton}
			</div>
		</div>
	`;

	modal.addEventListener('click', event => {
		if (
			event.target === modal ||
			event.target.closest('.favorite-city-rule-modal__close')
		) {
			closeFavoriteRuleModal();
		}
	});

	document.body.appendChild(modal);
}

async function requestJson(url) {
	const response = await fetch(url, {
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
		},
	});

	const text = await response.text();
	let data = null;
	let hasValidJson = text === '';

	try {
		if (text) {
			data = JSON.parse(text);
		}
		hasValidJson = true;
	} catch (e) {
		data = null;
	}

	if (response.redirected) {
		const error = new Error('A requisição de favoritos foi redirecionada');
		error.status = response.status;
		error.data = data;
		error.redirectUrl = response.url;
		throw error;
	}

	if (!response.ok) {
		const error = new Error(data?.message || data?.error || 'Erro ao salvar favorito');
		error.status = response.status;
		error.data = data;
		throw error;
	}

	if (!hasValidJson) {
		const error = new Error('Resposta inválida na requisição de favoritos');
		error.status = response.status;
		throw error;
	}

	return data;
}

async function fetchServerFavorites() {
	if (!SessionManager.isAuthenticated()) {
		serverFavorites = [];
		hasLoadedServerFavorites = true;
		return [];
	}

	const result = await requestJson(appLocalUrl('/json/myclimatempo/user/favoriteLocales'));
	if (!Array.isArray(result?.favoriteLocales)) {
		throw new Error('Resposta inválida ao carregar favoritos');
	}

	const favorites = result.favoriteLocales
		.map(mapBackendFavorite)
		.filter(Boolean);

	serverFavorites = favorites;
	hasLoadedServerFavorites = true;
	favorites.forEach(favorite => cacheValidFavorite(favorite.city));

	return serverFavorites;
}

async function refreshServerFavorites(force = false, options = {}) {
	if (!SessionManager.isAuthenticated()) {
		serverFavorites = [];
		hasLoadedServerFavorites = true;
		return [];
	}

	if (!force && hasLoadedServerFavorites) {
		return serverFavorites;
	}

	if (options.notify === false) {
		serverSyncNotificationSuppressed = true;
	} else {
		serverSyncShouldNotify = true;
	}

	if (!serverSyncPromise) {
		serverSyncPromise = fetchServerFavorites()
			.catch(error => {
				console.error('Erro ao sincronizar favoritos:', error);
				if (error.status === 401) {
					serverFavorites = [];
					hasLoadedServerFavorites = true;
				}
				return serverFavorites;
			})
			.finally(() => {
				const shouldNotify =
					serverSyncShouldNotify && !serverSyncNotificationSuppressed;
				serverSyncShouldNotify = false;
				serverSyncNotificationSuppressed = false;
				serverSyncPromise = null;
				if (shouldNotify) {
					dispatchFavoriteSync();
				}
			});
	}

	return serverSyncPromise;
}

async function persistAddFavorite(city) {
	const localeId = getCityStorageId(city);
	if (!localeId) {
		throw new Error('Cidade sem locale para favoritar');
	}

	return requestJson(
		appLocalUrl(`/json/myclimatempo/user/addFavoriteLocale?locale=${encodeURIComponent(localeId)}`),
	);
}

async function persistRemoveFavorite(city) {
	const localeId = getCityStorageId(city);
	if (!localeId) {
		throw new Error('Cidade sem locale para remover dos favoritos');
	}

	return requestJson(
		appLocalUrl(`/json/myclimatempo/user/removeFavoriteLocale?locale=${encodeURIComponent(localeId)}`),
	);
}

function cacheFavoriteAttempt(city) {
	FavoriteStorage.append(getFavoritePayload(city), 50, 'cid', FAVORITE_TYPE);
}

function cacheValidFavorite(city) {
	FavoriteStorage.append(getFavoritePayload(city), 50, 'cid', FAVORITE_TYPE);
}

function removeCachedFavorite(city) {
	const remaining = normalizeFavorites().filter(
		favorite => !isSameCity(favorite.city, city),
	);
	FavoriteStorage.set(remaining);
}

function getVisibleFavorites() {
	if (!SessionManager.isAuthenticated()) {
		return [];
	}

	return serverFavorites.map(favorite => ({
		...favorite,
		city: normalizeCityDisplay(favorite.city),
	}));
}

function isCityFavorited(city) {
	if (!SessionManager.isAuthenticated()) {
		return false;
	}

	if (!getCityStorageId(city)) {
		return false;
	}

	return serverFavorites.some(
		favorite => isSameCity(favorite.city, city),
	);
}

async function addFavorite(city) {
	cacheFavoriteAttempt(city);

	if (!SessionManager.isAuthenticated()) {
		setPendingFavorite(city, 'auth_required', null);
		notifyFavoriteAuthRequired(city);
		dispatchFavoriteChanged(city, false, 'auth_required');
		return { status: 'auth_required', isFavorited: false };
	}

	await refreshServerFavorites(true, { notify: false });
	const serverIds = getServerFavoriteIds();
	const cityId = getCityStorageId(city);

	if (serverIds.includes(cityId) || isCityFavorited(city)) {
		cacheValidFavorite(city);
		clearPendingFavorite(city);
		dispatchFavoriteChanged(city, true, 'already_exists');
		return { status: 'already_exists', isFavorited: true };
	}

	const limit = getActiveLimit();
	if (Number.isFinite(limit) && serverIds.length >= limit) {
		setPendingFavorite(city, 'limit_reached');
		notifyFavoriteLimitReached(city);
		dispatchFavoriteChanged(city, false, 'limit_reached');
		return { status: 'limit_reached', isFavorited: false };
	}

	try {
		await persistAddFavorite(city);
		await refreshServerFavorites(true);
		if (!isCityFavorited(city)) {
			throw new Error('Favorito não confirmado pelo servidor');
		}
		cacheValidFavorite(city);
		clearPendingFavorite(city);
		dispatchFavoriteChanged(city, true, 'added');
		return {
			status: 'added',
			isFavorited: true,
			serverIds: getServerFavoriteIds(),
		};
	} catch (error) {
		if (error.status === 409) {
			await refreshServerFavorites(true);
			setPendingFavorite(city, 'limit_reached');
			notifyFavoriteLimitReached(city);
			dispatchFavoriteChanged(city, false, 'limit_reached');
			return { status: 'limit_reached', isFavorited: false };
		}

		console.error('Erro ao adicionar favorito:', error);
		document.dispatchEvent(
			new CustomEvent('favoritePersistFailed', {
				detail: { city, action: 'add', error },
			}),
		);
		dispatchFavoriteChanged(city, false, 'persist_failed');
		return { status: 'error', isFavorited: false, error };
	}
}

async function removeFavorite(city) {
	if (!SessionManager.isAuthenticated()) {
		removeCachedFavorite(city);
		clearPendingFavorite(city);
		dispatchFavoriteChanged(city, false, 'removed');
		return { status: 'removed', isFavorited: false };
	}

	const cityId = getCityStorageId(city);
	await refreshServerFavorites(true, { notify: false });
	const serverIds = getServerFavoriteIds();
	const relatedServerIds = new Set(cityId ? [cityId] : []);
	serverFavorites.forEach(favorite => {
		if (!isSameCity(favorite.city, city)) {
			return;
		}

		const relatedId = getCityStorageId(favorite.city);
		if (relatedId) {
			relatedServerIds.add(relatedId);
		}
	});

	try {
		for (const relatedId of relatedServerIds) {
			if (!serverIds.includes(relatedId)) {
				continue;
			}

			await persistRemoveFavorite({ cloc: relatedId });
		}

		await refreshServerFavorites(true);
		if (isCityFavorited(city)) {
			throw new Error('Remoção do favorito não confirmada pelo servidor');
		}
		removeCachedFavorite(city);
		clearPendingFavorite(city);
		dispatchFavoriteChanged(city, false, 'removed');
		return { status: 'removed', isFavorited: false };
	} catch (error) {
		console.error('Erro ao remover favorito:', error);
		document.dispatchEvent(
			new CustomEvent('favoritePersistFailed', {
				detail: { city, action: 'remove', error },
			}),
		);
		dispatchFavoriteChanged(city, true, 'persist_failed');
		return { status: 'error', isFavorited: true, error };
	}
}

async function toggleFavorite(city) {
	return isCityFavorited(city) ? removeFavorite(city) : addFavorite(city);
}

async function fulfillPendingFavorite(trigger = 'auth') {
	if (!SessionManager.isAuthenticated()) {
		return { status: 'anonymous' };
	}

	const pending = getPendingFavorite();
	if (!pending) {
		return { status: 'no_pending' };
	}

	const userId = String(SessionManager.getUserId() || '');
	if (pending.userId && pending.userId !== userId) {
		clearPendingFavorite();
		return { status: 'owner_mismatch' };
	}

	const city = pending.city;
	setPendingFavorite(city, pending.reason || trigger, userId);
	await refreshServerFavorites(true, { notify: false });

	if (isCityFavorited(city)) {
		cacheValidFavorite(city);
		clearPendingFavorite(city);
		dispatchFavoriteChanged(city, true, 'already_exists');
		return { status: 'already_exists', isFavorited: true };
	}

	const serverIds = getServerFavoriteIds();
	const limit = getActiveLimit();
	const canBypassClientLimit = trigger === 'subscription';
	if (
		!canBypassClientLimit &&
		Number.isFinite(limit) &&
		serverIds.length >= limit
	) {
		setPendingFavorite(city, 'limit_reached', userId);
		return { status: 'limit_reached', isFavorited: false };
	}

	try {
		await persistAddFavorite(city);
		await refreshServerFavorites(true);

		if (!isCityFavorited(city)) {
			throw new Error('Favorito pendente não confirmado pelo servidor');
		}

		cacheValidFavorite(city);
		clearPendingFavorite(city);
		dispatchFavoriteChanged(city, true, 'pending_added');
		return { status: 'added', isFavorited: true };
	} catch (error) {
		if (error.status === 409) {
			setPendingFavorite(city, 'limit_reached', userId);
			return { status: 'limit_reached', isFavorited: false, error };
		}

		console.error('Erro ao salvar favorito pendente:', error);
		document.dispatchEvent(
			new CustomEvent('favoritePersistFailed', {
				detail: { city, action: 'fulfill_pending', error },
			}),
		);
		return { status: 'error', isFavorited: false, error };
	}
}

const FavoriteCityService = {
	LOGGED_FAVORITE_LIMIT,
	addFavorite,
	removeFavorite,
	toggleFavorite,
	isCityFavorited,
	getVisibleFavorites,
	refreshServerFavorites,
	fulfillPendingFavorite,
	getCityStorageId,
	getActiveLimit,
	isClimatempoPlus,
};

export default FavoriteCityService;
